"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

import { useAuthStore } from "@/stores/auth-store";
import { useWorkspaceStore } from "@/stores/workspace-store";

/**
 * Put WHO an error happened to on every frontend Sentry event.
 *
 * Until 2026-07-30 no frontend event carried any identity — `sendDefaultPii`
 * is false and nothing ever called `Sentry.setUser`, so every event was
 * anonymous and "which customer hit this" could not be answered from Sentry
 * on a multi-tenant platform.
 *
 * WHY A SUBSCRIBER AND NOT A CALL IN login()/logout()
 * --------------------------------------------------
 * The auth store is `persist`ed, so a returning user is authenticated by
 * REHYDRATION, not by `login()` — patching the mutators would leave every
 * reloaded tab anonymous, which is most of them. Workspace switching
 * (`setWorkspace`) and domain switching are separate mutators too. Reacting to
 * the state itself covers login, logout, rehydration and every switch, and
 * cannot drift when someone adds a fifth way to change it.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * IDs ONLY — never email, name, or company name.
 * ─────────────────────────────────────────────────────────────────────────
 * `AuthUser` carries `email`, `firstName`, `lastName` and `companyName`. None
 * of them go to Sentry: `sendDefaultPii: false` is deliberate because this app
 * handles recipient emails and customer mail, and Sentry must not become a
 * shadow copy of it. Opaque UUIDs answer "one tenant or all of them" without
 * putting a person on the wire. This mirrors the API
 * (apps/api/src/common/sentry/tenant-scope.ts) so an event from either side
 * filters the same way.
 */
export function SentryTenantScope() {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const companyId = useAuthStore((s) => s.user?.companyId ?? null);
  // Wave-3 item 4 (audit 2026-09-25) — the LIVE tenant, from the workspace
  // store the switcher drives. The auth store's workspaceId is the login-time
  // one (it never follows a switch) and its domainId is never set, so events
  // were tagged with a workspace the user had left and no domain at all.
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const domainId = useWorkspaceStore((s) => s.activeDomainId);

  useEffect(() => {
    // Logged out: clear, so the next anonymous error is not attributed to the
    // person who happened to use this browser last.
    if (!userId) {
      Sentry.setUser(null);
      Sentry.setTag("company_id", undefined);
      Sentry.setTag("workspace_id", undefined);
      Sentry.setTag("domain_id", undefined);
      return;
    }

    Sentry.setUser({ id: userId });
    Sentry.setTag("company_id", companyId ?? undefined);
    Sentry.setTag("workspace_id", workspaceId ?? undefined);
    Sentry.setTag("domain_id", domainId ?? undefined);
  }, [userId, companyId, workspaceId, domainId]);

  return null;
}
